import React, { useEffect, useState, useCallback } from 'react';
import './BookingCalendar.css';
import BookingDay from './CalendarDay';
import Header from './Header';
import { useNavigate, useLocation } from 'react-router-dom';
import { authHeader } from "./helpers/auth-header";
import { BS_BASE } from "./configs/api";

/* ================= Calendar (Month) ================= */
function Calendar({ onDateSelect, availableDates, onMonthChange, allGreen, disableSelection }) {
  const today = new Date();
  const [selected, setSelected] = useState(null);
  const [viewDate, setViewDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));

  // Notify parent whenever the visible month changes
  useEffect(() => {
    onMonthChange?.(viewDate);
  }, [viewDate, onMonthChange]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const days = [];
  for (let i = 0; i < firstDay; i++) {
    days.push(<div key={`empty-${i}`} className="calendar-day empty" />);
  }

  for (let d = 1; d <= daysInMonth; d++) {
    const isToday = d === today.getDate() && month === today.getMonth() && year === today.getFullYear();
    const isSelected = selected === d;
    const isPast =
      year < today.getFullYear() ||
      (year === today.getFullYear() && month < today.getMonth()) ||
      (year === today.getFullYear() && month === today.getMonth() && d < today.getDate());

    const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const isAvailable = availableDates?.has(iso) && !isPast;
    const isAllGreen = allGreen && !isPast;

    days.push(
      <div
        key={d}
        className={
          `calendar-day` +
          (isSelected ? ' selected' : '') +
          (isPast ? ' disabled' : '') +
          (isToday ? ' today' : '') +
          (isAvailable ? ' available' : '') +
          (isAllGreen ? ' all-green' : '')
        }
        onClick={() => {
          if (!isPast && !isToday && !disableSelection) {
            setSelected(d);
            onDateSelect(new Date(year, month, d));
          }
        }}
        style={
          (isPast || isToday)
            ? { pointerEvents: 'none', opacity: isPast ? 0.5 : 1 }
            : (disableSelection ? { cursor: 'not-allowed', opacity: 0.6 } : {})
        }
        title={isAvailable ? 'Slots available' : undefined}
      >
        {d}
      </div>
    );
  }

  return (
    <div className="calendar">
      <div className="calendar-header">
        <button className="calendar-nav" onClick={() => { setViewDate(new Date(year, month - 1, 1)); setSelected(null); }}>&lt;</button>
        <span>{viewDate.toLocaleString('default', { month: 'long' })} {year}</span>
        <button className="calendar-nav" onClick={() => { setViewDate(new Date(year, month + 1, 1)); setSelected(null); }}>&gt;</button>
      </div>
      <div className="calendar-weekdays">
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(w => <div key={w} className="calendar-weekday">{w}</div>)}
      </div>
      <div className="calendar-grid">{days}</div>
    </div>
  );
}

/* ================= Booking Calendar Wrapper ================= */
function BookingCal() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableDates, setAvailableDates] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [loadingClients, setLoadingClients] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isSchool = location.state && location.state.school === true;
  const isSingleLesson = location.state && location.state.singleLesson === true;

  const formatDMY = (date) =>
    `${String(date.getDate()).padStart(2,'0')}/${String(date.getMonth()+1).padStart(2,'0')}/${date.getFullYear()}`;

  const normalizeToISO = (raw) => {
    if (!raw || typeof raw !== 'string') return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
      const [d,m,y] = raw.split('/');
      return `${y}-${m}-${d}`;
    }
    return null;
  };

  // Fetch availability for a specific visible month
  const fetchMonthAvailability = useCallback(async (visibleMonthDate) => {
    setLoading(true);
    setError("");
    try {
      // Start = tomorrow (midnight)
      const start = new Date();
      start.setDate(start.getDate() + 1); // add 1 day onto start
      start.setHours(0, 0, 0, 0);

      // End = start + 3 weeks (21 days)
      const end = new Date(start);
      end.setDate(end.getDate() + 21);

      // Format both as dd/MM/yyyy
      const startStr = formatDMY(start);
      const endStr = formatDMY(end);

      // Clear old availability
      setAvailableDates(new Set());

      const res = await fetch(
        `${BS_BASE}/available-slots-by-range?StartDate=${encodeURIComponent(startStr)}&EndDate=${encodeURIComponent(endStr)}&isSchool=${isSingleLesson ? true : isSchool}`,
        { headers: { Accept: 'application/json', ...authHeader() } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);

      const dateSet = new Set();
      list.forEach(item => {
        let raw = item;
        if (item && typeof item === 'object') raw = item.date || item.day || item.startDate;
        const iso = normalizeToISO(raw);
        if (iso) dateSet.add(iso);
      });
      setAvailableDates(dateSet);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setAvailableDates(new Set());
    } finally {
      setLoading(false);
    }
  }, [isSchool, isSingleLesson]);

  useEffect(() => {
    // Initial load for current month
    fetchMonthAvailability(new Date());
  }, [fetchMonthAvailability]);

  // Load clients when in single-lesson mode
  useEffect(() => {
    if (!isSingleLesson) return;
    let ignore = false;
    async function loadClients() {
      setLoadingClients(true);
      try {
        const res = await fetch(`${BS_BASE}/clients`, {
          headers: { Accept: "application/json", ...authHeader() }
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!ignore) {
          const list = Array.isArray(data) ? data : (Array.isArray(data && data.items) ? data.items : []);
          const normalized = list
            .map((c) => {
              const id =
                c.ID != null
                  ? c.ID
                  : c.id != null
                  ? c.id
                  : c.clientId != null
                  ? c.clientId
                  : c.userId;
              const name = [c.firstName, c.lastName].filter(Boolean).join(" ");
              return { id, name };
            })
            .filter((c) => c.id != null && c.name !== "");
          setClients(normalized);
        }
      } catch (e) {
        if (!ignore) {
          // surface client loading errors via generic error banner
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (!ignore) setLoadingClients(false);
      }
    }
    loadClients();
    return () => {
      ignore = true;
    };
  }, [isSingleLesson]);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    navigate('/calendarDay', {
      state: {
        selectedDate: date,
        // When single-lesson, we still *book* a lesson, but
        // the day view will fetch school availability explicitly.
        isSchool,
        isSingleLesson,
        clientId: isSingleLesson ? clientId : undefined
      }
    });
  };

  const handleBack = () => {
    if (selectedDate) {
      setSelectedDate(null);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="bookingcal-page">
      <div className="bookingcal-container">
        <Header onBack={handleBack} />
        <h2 style={{ marginTop: 0 }}>{isSchool ? 'School Calendar' : 'Lesson Calendar'}</h2>

        {isSingleLesson && (
          <div style={{ marginBottom: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <label htmlFor="singleLessonClient" style={{ fontWeight: 700 }}>Client</label>
            <select
              id="singleLessonClient"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              disabled={loadingClients}
              style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', minWidth: 220 }}
            >
              <option value="">{loadingClients ? 'Loading clients…' : 'Select a client'}</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        )}
        {loading && <div style={{ fontSize: 14, color: '#444' }}>Loading availability…</div>}
        {error && <div style={{ fontSize: 12, color: 'crimson' }}>Error: {error}</div>}
        {!selectedDate ? (
          <Calendar
            onDateSelect={handleDateSelect}
            availableDates={availableDates}
            onMonthChange={fetchMonthAvailability}
            allGreen={isSingleLesson}
            disableSelection={isSingleLesson && !clientId}
          />
        ) : (
          <BookingDay date={selectedDate} onBack={handleBack} />
        )}
      </div>
    </div>
  );
}

export default BookingCal;